import type { NostrEvent } from 'nostr-tools/core';

// Memoization cache for difficulty calculations
const difficultyCache = new Map<string, number>();

/**
 * Extract POW difficulty from a nostr event (NIP-13)
 * Counts the number of leading zero bits in the event ID
 */
export function getPowDifficulty(event: NostrEvent): number {
  return getPowDifficultyFromId(event.id);
}

/**
 * Check if an event has a valid POW commitment (nonce tag)
 */
export function hasValidPow(event: NostrEvent, minDifficulty: number = 0): boolean {
  // Check for nonce tag
  const nonceTag = event.tags.find((t) => t[0] === 'nonce');
  if (!nonceTag) return false;

  // Verify the difficulty
  const difficulty = getPowDifficulty(event);
  return difficulty >= minDifficulty;
}

/**
 * Get the POW target difficulty from nonce tag
 */
export function getTargetDifficulty(event: NostrEvent): number | null {
  const nonceTag = event.tags.find((t) => t[0] === 'nonce');
  if (!nonceTag || !nonceTag[2]) return null;
  return parseInt(nonceTag[2], 10);
}

/**
 * Calculate POW difficulty of a pubkey (count leading zeros)
 */
export function getPubkeyPowDifficulty(pubkey: string): number {
  let count = 0;
  for (const char of pubkey) {
    if (char === '0') count++;
    else break;
  }
  return count;
}

/**
 * Get POW difficulty from event ID (memoized)
 * Useful for profile events where we just have the ID
 */
export function getPowDifficultyFromId(eventId: string): number {
  // Check cache first
  if (difficultyCache.has(eventId)) {
    return difficultyCache.get(eventId)!;
  }

  // Calculate difficulty
  let count = 0;
  for (let i = 0; i < eventId.length; i++) {
    const nibble = parseInt(eventId[i], 16);
    if (nibble === 0) {
      count += 4;
      continue;
    }

    // Count leading zeros in the non-zero nibble
    if (nibble >= 8) count += 0; // 1xxx
    else if (nibble >= 4) count += 1; // 01xx
    else if (nibble >= 2) count += 2; // 001x
    else count += 3; // 0001
    break;
  }

  // Cache the result
  difficultyCache.set(eventId, count);
  return count;
}

/**
 * Clear the difficulty cache (useful for testing)
 */
export function clearDifficultyCache(): void {
  difficultyCache.clear();
}

/**
 * Format POW difficulty for display
 */
export function formatPowDifficulty(difficulty: number): string {
  if (difficulty === 0) return 'none';
  if (difficulty < 16) return `💎 ${difficulty}`;
  if (difficulty < 20) return `💎💎 ${difficulty}`;
  if (difficulty < 24) return `💎💎💎 ${difficulty}`;
  if (difficulty < 28) return `💎💎💎💎 ${difficulty}`;
  if (difficulty < 32) return `💎💎💎💎💎 ${difficulty}`;
  if (difficulty < 36) return `💎💎💎💎💎💎 ${difficulty}`;
  if (difficulty < 40) return `💎💎💎💎💎💎💎 ${difficulty}`;
  return `🤯💎💎💎💎💎💎💎💎 ${difficulty}`;
}
